<template>
  <div class="json-kv-editor" :class="{ 'has-error': parseError }" :data-json-field="fieldKey">
    <b-table-simple small bordered responsive class="mb-1">
      <b-thead>
        <b-tr>
          <b-th style="width: 40%">{{ keyLabel }}</b-th>
          <b-th style="width: 50%">{{ valueLabel }}</b-th>
          <b-th style="width: 10%"></b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="(row, idx) in rows" :key="idx">
          <b-td>
            <b-form-input
              size="sm"
              v-model="row.key"
              :placeholder="keyPlaceholder"
              @input="emitUpdate"
            />
          </b-td>
          <b-td>
            <b-form-input
              size="sm"
              v-model="row.val"
              :type="valueType === 'number' ? 'number' : 'text'"
              :placeholder="valuePlaceholder"
              @input="emitUpdate"
            />
          </b-td>
          <b-td class="text-center align-middle">
            <b-button size="sm" variant="outline-danger" @click="removeRow(idx)" title="Remove">
              &times;
            </b-button>
          </b-td>
        </b-tr>
        <b-tr v-if="rows.length === 0">
          <b-td colspan="3" class="text-center text-muted" style="font-style: italic; font-size: 13px;">
            No entries. Click "Add Row" below.
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <div class="d-flex align-items-center" style="gap: 8px;">
      <b-button size="sm" variant="outline-primary" @click="addRow">+ Add Row</b-button>
      <b-button v-if="allowRawJson" size="sm" variant="outline-secondary" @click="showRaw = !showRaw">
        {{ showRaw ? 'Hide' : 'Show' }} Raw JSON
      </b-button>
      <span v-if="parseError" class="text-danger" style="font-size: 12px;">{{ parseError }}</span>
    </div>
    <b-form-textarea
      v-if="allowRawJson && showRaw"
      :value="value"
      @input="onRawInput"
      rows="3"
      class="mt-2"
      style="font-family: monospace; font-size: 13px;"
    />
  </div>
</template>

<script>
export default {
  name: 'JsonKeyValueEditor',
  props: {
    value: { type: String, default: '' },
    fieldKey: { type: String, default: '' },
    keyLabel: { type: String, default: 'Key' },
    valueLabel: { type: String, default: 'Value' },
    keyPlaceholder: { type: String, default: '' },
    valuePlaceholder: { type: String, default: '' },
    valueType: { type: String, default: 'string' },
    allowRawJson: { type: Boolean, default: false },
  },
  created() {
    this._parseTimer = null
  },
  beforeDestroy() {
    if (this._parseTimer) clearTimeout(this._parseTimer)
  },
  data() {
    return {
      rows: [],
      showRaw: false,
      parseError: '',
      internalUpdate: false,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (this.internalUpdate) {
          this.internalUpdate = false
          return
        }
        this.parseValue(newVal)
      },
    },
    parseError(val) {
      if (val && this.allowRawJson) this.showRaw = true
    },
    allowRawJson(val) {
      if (!val) {
        this.showRaw = false
        if (this._parseTimer) {
          clearTimeout(this._parseTimer)
          this._parseTimer = null
        }
      }
    },
  },
  methods: {
    parseValue(jsonStr) {
      this.parseError = ''
      if (!jsonStr || jsonStr.trim() === '') {
        this.rows = []
        this.emitValidation('')
        return
      }
      try {
        const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
          this.rows = Object.entries(obj).map(([key, val]) => ({
            key,
            val: String(val),
          }))
          this.emitValidation('')
        } else {
          this.parseError = 'Expected a JSON object (not array or primitive)'
          this.rows = []
          this.emitValidation(this.parseError)
        }
      } catch (e) {
        this.parseError = 'Invalid JSON: ' + e.message
        this.rows = []
        this.emitValidation(this.parseError)
      }
    },
    buildObject() {
      const obj = {}
      for (const row of this.rows) {
        const k = (row.key || '').trim()
        if (!k) continue
        if (this.valueType === 'number') {
          const num = Number(row.val)
          obj[k] = isNaN(num) ? row.val : num
        } else {
          obj[k] = row.val
        }
      }
      return obj
    },
    emitUpdate() {
      this.internalUpdate = true
      const obj = this.buildObject()
      const json = Object.keys(obj).length > 0 ? JSON.stringify(obj) : ''
      this.$emit('input', json)
    },
    onRawInput(val) {
      this.internalUpdate = true
      this.$emit('input', val)
      if (this._parseTimer) clearTimeout(this._parseTimer)
      this._parseTimer = setTimeout(() => {
        this.parseValue(val)
      }, 1000)
    },
    addRow() {
      this.rows.push({ key: '', val: '' })
    },
    removeRow(idx) {
      this.rows.splice(idx, 1)
      this.emitUpdate()
    },
    emitValidation(error) {
      if (this.fieldKey) {
        this.$emit('validation', this.fieldKey, error)
      }
    },
  },
}
</script>

<style scoped>
.json-kv-editor .table {
  margin-bottom: 4px;
}
.json-kv-editor .table td {
  padding: 4px 6px;
  vertical-align: middle;
}
.json-kv-editor .table th {
  padding: 4px 6px;
  font-size: 13px;
  font-weight: 600;
  background: #f8f9fa;
}
.json-kv-editor.has-error .table {
  border-color: #dc3545;
}
</style>
